/**
 * activity-log.ts — thin write-only helper for the `activity_log` table.
 *
 * Pattern mirrors Sub3 / Sub4-redo:
 *   - fire-and-forget: callers do `void logActivity({...})`, never await
 *   - never throw to the caller — surface errors via toast + console.error
 *   - no localStorage cache; this is an append-only audit feed
 *
 * Schema (see VERIFY.sql / C7 migration):
 *   id          uuid PK default gen_random_uuid()
 *   tenant_id   bigint NOT NULL
 *   user_id     uuid   NOT NULL    -- auth.uid() — the "owner_id" for per-user filtering
 *   action      text   NOT NULL    -- e.g. 'ae.daily_log', 'ae.call_logged'
 *   entity_type text                -- 'daily_log' | 'account' | 'prospect' | ...
 *   entity_id   text                -- free-form (uuid OR numeric id stringified)
 *   payload     jsonb               -- small structured context (<= 4 KB)
 *   created_at  timestamptz default now()
 *
 * RLS (Wave F Option 2 — uses aip_current_tenant_id()):
 *   INSERT: authenticated user; tenant_id auto-stamped via RLS
 *   SELECT: own rows (user_id = auth.uid()) + manager/executive in same tenant
 *
 * BUILD-13 Item 1 fix (2026-05-15):
 *   - Fixed s.userId → s.id (SessionUser.id is the correct field name)
 *   - Expanded ActivityAction enum to cover all AE workflow actions
 */

import { supabase, getSession } from "./session";

// ── Toast injection (same pattern as Sub3 / Sub4) ─────────────────────────

type ToastFn = (msg: string, kind?: "error" | "warning" | "info" | "success") => void;
let _toast: ToastFn | null = null;
export function setToastHandler(fn: ToastFn | null): void {
  _toast = fn;
}
function surface(scope: string, err: unknown): void {
  const msg = err instanceof Error ? err.message : String(err);
  // eslint-disable-next-line no-console
  console.error(`[activity-log] ${scope}:`, err);
  if (_toast) _toast(`Activity log write failed (${scope})`, "warning");
}

// ── Public API ────────────────────────────────────────────────────────────

export type ActivityAction =
  // Auth
  | "auth.login"
  // AE daily log (BUILD-13 Item 1)
  | "ae.daily_log"           // full daily log save (summary row)
  | "ae.call_logged"         // individual call entry in daily log
  | "ae.email_sent"          // email sent entry in daily log
  | "ae.meeting_logged"      // meeting entry in daily log
  | "ae.demo_logged"         // demo given entry in daily log
  | "ae.proposal_sent"       // proposal sent entry
  | "ae.contract_sent"       // contract sent entry
  | "ae.deal_advanced"       // deal stage advanced
  | "ae.account_updated"     // per-account activity entry logged
  | "ae.prospect_added"      // prospect/account added during log session
  // Plan / account (pre-existing)
  | "plan.save"
  | "plan.status_change"
  | "mpact.answer_save"
  // Cadence (pre-existing)
  | "cadence.enroll"
  | "cadence.step.complete"
  // Daily activity (pre-existing generic)
  | "daily_activity.log";

export interface LogActivityInput {
  action: ActivityAction | string;
  entity_type?: string;
  entity_id?: string | number | null;
  payload?: Record<string, unknown>;
}

/**
 * Fire-and-forget activity log write.
 * Callers should use `void logActivity({...})` and never await.
 *
 * FIX (BUILD-13 Item 1): was using s.userId (undefined on SessionUser).
 * SessionUser.id is the correct Supabase auth UUID field.
 */
export async function logActivity(input: LogActivityInput): Promise<void> {
  try {
    const s = getSession();
    if (!s) return; // unauthenticated — nothing to log

    const row = {
      tenant_id: s.tenantId,
      user_id:   s.id,          // FIX: was s.userId (undefined); SessionUser uses .id
      action:    input.action,
      entity_type: input.entity_type ?? null,
      entity_id:   input.entity_id == null ? null : String(input.entity_id),
      payload:     input.payload ?? {},
    };

    const { error } = await supabase().from("activity_log").insert(row);
    if (error) throw new Error(error.message);
  } catch (err) {
    surface(String(input.action), err);
  }
}

/**
 * Read recent activity_log rows for the current user.
 * Returns up to `limit` rows ordered newest-first.
 * Used by AeActivityLog and ActivityAudit components.
 */
export async function fetchMyActivity(limit = 50): Promise<{
  id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}[]> {
  try {
    const s = getSession();
    if (!s) return [];

    const { data, error } = await supabase()
      .from("activity_log")
      .select("id, action, entity_type, entity_id, payload, created_at")
      .eq("user_id", s.id)
      .eq("tenant_id", s.tenantId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("[activity-log] fetchMyActivity:", error.message);
      return [];
    }
    return (data ?? []) as any[];
  } catch {
    return [];
  }
}

/**
 * Read recent activity_log rows for any user in the current tenant.
 * Manager / executive only — RLS enforces access.
 */
export async function fetchTenantActivity(
  userId?: string,
  limit = 100,
): Promise<{
  id: string;
  user_id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}[]> {
  try {
    const s = getSession();
    if (!s) return [];

    let query = supabase()
      .from("activity_log")
      .select("id, user_id, action, entity_type, entity_id, payload, created_at")
      .eq("tenant_id", s.tenantId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (userId) {
      query = query.eq("user_id", userId);
    }

    const { data, error } = await query;

    if (error) {
      console.error("[activity-log] fetchTenantActivity:", error.message);
      return [];
    }
    return (data ?? []) as any[];
  } catch {
    return [];
  }
}
